import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
    AreaChart,
    Area,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    PieChart,
    Pie,
    Cell,
    LineChart,
    Line,
} from 'recharts';
import Cookies from 'js-cookie';
import axios from 'axios';
import apiConfig, { API_BASE_URL as NAMED_API_BASE_URL } from '../../../config/api'
import Topbar from '../../layouts/Topbar';
import Sidebar from '../../layouts/Sidebar';

/* ------------------------------------------------------------------ */
/*  Shimmer / skeleton helpers (matches original codebase pattern)     */
/* ------------------------------------------------------------------ */
const Shimmer = ({ width = '100%', height = '1rem', className = '', rounded = '4px' }) => (
    <span
        className={`placeholder placeholder-wave col-12 ${className}`}
        style={{ width, height, minHeight: height, borderRadius: rounded, display: 'block' }}
    />
);

const DashboardSkeleton = () => (
    <div className="page-wrapper">
        <div className="content pb-0">
            <div className="d-flex align-items-center justify-content-between mb-4">
                <div className="placeholder-glow">
                    <Shimmer width="180px" height="24px" className="mb-2" />
                    <Shimmer width="300px" height="14px" />
                </div>
                <div className="d-flex gap-2 placeholder-glow">
                    <Shimmer width="90px" height="32px" rounded="6px" />
                </div>
            </div>

            {/* KPI row skeleton */}
            <div className="row">
                {[0, 1, 2, 3].map((i) => (
                    <div key={i} className="col-md-6 col-xl-3 d-flex" style={{ animationDelay: `${i * 80}ms` }}>
                        <div className="card flex-fill">
                            <div className="card-body placeholder-glow">
                                <Shimmer width="48px" height="48px" rounded="12px" className="mb-3" />
                                <Shimmer width="60%" height="14px" className="mb-2" />
                                <Shimmer width="40%" height="28px" />
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {/* Charts row skeleton */}
            <div className="row">
                {[0, 1].map((i) => (
                    <div key={i} className="col-md-6 d-flex" style={{ animationDelay: `${320 + i * 80}ms` }}>
                        <div className="card flex-fill">
                            <div className="card-header placeholder-glow">
                                <Shimmer width="45%" height="18px" />
                            </div>
                            <div className="card-body placeholder-glow">
                                <Shimmer width="100%" height="220px" rounded="8px" />
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {/* Table skeleton */}
            <div className="card mt-3">
                <div className="card-header placeholder-glow">
                    <Shimmer width="40%" height="18px" />
                </div>
                <div className="card-body placeholder-glow">
                    <Shimmer width="100%" height="14px" className="mb-3" />
                    {Array.from({ length: 5 }).map((_, i) => (
                        <Shimmer key={i} width="100%" height="40px" className="mb-2" rounded="6px" />
                    ))}
                </div>
            </div>
        </div>
    </div>
);

/* ------------------------------------------------------------------ */
/*  Staggered card reveal (matches original AnimatedCard pattern)      */
/* ------------------------------------------------------------------ */
const AnimatedCard = ({ children, index = 0, className = '' }) => (
    <div
        className={`stats-card-reveal ${className}`}
        style={{ animationDelay: `${index * 70}ms` }}
    >
        {children}
    </div>
);

/* ------------------------------------------------------------------ */
/*  Chart colours                                                      */
/* ------------------------------------------------------------------ */
const PIE_COLORS = ['#4F46E5', '#818CF8', '#A5B4FC', '#C7D2FE'];
const REPORTING_COLORS = ['#4F46E5', '#ef4444'];

/* ------------------------------------------------------------------ */
/*  Custom pie label                                                   */
/* ------------------------------------------------------------------ */
const renderPieLabel = ({ cx, cy, midAngle, outerRadius, percent, name }) => {
    const RADIAN = Math.PI / 180;
    const radius = outerRadius + 20;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);
    return (
        <text
            x={x}
            y={y}
            fill="#374151"
            textAnchor={x > cx ? 'start' : 'end'}
            dominantBaseline="central"
            fontSize={12}
            fontWeight={600}
        >
            {`${name}: ${(percent * 100).toFixed(0)}%`}
        </text>
    );
};

/* ------------------------------------------------------------------ */
/*  Main Dashboard component                                           */
/* ------------------------------------------------------------------ */
const API_BASE_URL = typeof apiConfig === 'string'
    ? apiConfig
    : (NAMED_API_BASE_URL || 'https://pms.burrayhr.com')

const Dashboard = () => {

    /* ── Performance analytics ── */
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [performanceData, setPerformanceData] = useState(null);

    /* ── Reporting summary ── */
    const [statusSummary, setStatusSummary] = useState({ status_distribution: [] });
    const [statusLoading, setStatusLoading] = useState(true);
    const [statusError, setStatusError] = useState(null);

    /* ── Recent reviews ── */
    const [loadingReviews, setLoadingReviews] = useState(true);
    const [reviewsError, setReviewsError] = useState(null);
    const [reviews, setReviews] = useState([]);

    /* ── Trend year selector ── */
    const [trendYearDropdownOpen, setTrendYearDropdownOpen] = useState(false);
    const [trendDataYear, setTrendDataYear] = useState(new Date().getFullYear());
    const trendDropdownRef = useRef(null);
    const fetchIntervalRef = useRef(null);

    /* ── Fetch performance analytics ── */
    const fetchData = useCallback(
        async (showLoading = true, year = trendDataYear) => {
            if (showLoading) setLoading(true);
            setRefreshing(true);
            try {
                const token = Cookies.get('token');
                const response = await axios.get(`${API_BASE_URL}/api/performance-analytics?year=${year}`, {
                    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
                });
                console.log(response.data)
                setPerformanceData(response.data);
            } catch {
                setPerformanceData(null);
            } finally {
                if (showLoading) setLoading(false);
                setRefreshing(false);
            }
        },
        [API_BASE_URL, trendDataYear]
    );

    /* ── Fetch reporting summary ── */
    const fetchStatusSummary = useCallback(async () => {
        setStatusLoading(true);
        setStatusError(null);
        try {
            const token = Cookies.get('token');
            const response = await axios.get(`${API_BASE_URL}/api/reporting/summary`, {
                headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
            });
            setStatusSummary(response.data);
        } catch {
            setStatusError('Failed to load status distribution');
            setStatusSummary({ status_distribution: [] });
        } finally {
            setStatusLoading(false);
        }
    }, [API_BASE_URL]);

    /* ── Fetch recent reviews ── */
    useEffect(() => {
        const fetchReviews = async () => {
            setLoadingReviews(true);
            setReviewsError(null);
            try {
                const token = Cookies.get('token');
                const currentYear = new Date().getFullYear();
                const response = await axios.get(`${API_BASE_URL}/api/goals/`, {
                    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
                    params: { year: currentYear },
                });
                const completed = (Array.isArray(response.data) ? response.data : [])
                    .filter((g) => (g.status === 'complete' || g.status === 'completed') && Number(g.hod_score) > 0);
                completed.sort(
                    (a, b) =>
                        new Date(b.updated_at || b.completed_at || b.created_at) -
                        new Date(a.updated_at || a.completed_at || a.created_at)
                );
                setReviews(completed);
            } catch {
                setReviewsError('Failed to fetch recent reviews.');
            } finally {
                setLoadingReviews(false);
            }
        };
        fetchReviews();
    }, [API_BASE_URL]);

    /* ── Initial fetch + polling ── */
    useEffect(() => {
        fetchData(true, trendDataYear);
        fetchStatusSummary();
        fetchIntervalRef.current = setInterval(() => {
            fetchData(false, trendDataYear);
            fetchStatusSummary();
        }, 30000);
        return () => clearInterval(fetchIntervalRef.current);
    }, [fetchData, fetchStatusSummary, trendDataYear]);

    /* ── Close year dropdown on outside click ── */
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (trendDropdownRef.current && !trendDropdownRef.current.contains(e.target)) {
                setTrendYearDropdownOpen(false);
            }
        };
        if (trendYearDropdownOpen) document.addEventListener('mousedown', handleClickOutside);
        else document.removeEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [trendYearDropdownOpen]);

    /* ── Derived data ── */
    const totalGoals = useMemo(
        () => statusSummary?.status_distribution?.reduce((sum, item) => sum + item.count, 0) || 0,
        [statusSummary]
    );

    const reportingPieData = [
        { name: 'Reported', value: statusSummary.reported_goals },
        { name: 'Not Reported', value: statusSummary.not_reported },
    ];

    /* ── Styles for page animations ── */
    const animStyles = `
        .stats-card-reveal {
            animation: statsFadeInUp 0.45s ease both;
        }
        @keyframes statsFadeInUp {
            from { opacity: 0; transform: translateY(12px); }
            to   { opacity: 1; transform: translateY(0); }
        }
        .placeholder-glow .placeholder {
            animation: placeholder-glow 1.4s ease-in-out infinite;
        }
        .year-dropdown {
            position: absolute;
            top: calc(100% + 4px);
            right: 0;
            background: #fff;
            border: 1px solid #e5e7eb;
            border-radius: 8px;
            box-shadow: 0 4px 16px rgba(0,0,0,0.1);
            z-index: 100;
            min-width: 100px;
        }
        .year-dropdown-item {
            padding: 8px 16px;
            cursor: pointer;
            font-size: 14px;
            transition: background 0.15s;
        }
        .year-dropdown-item:hover { background: #f3f4f6; }
        .year-dropdown-item.active { background: #4F46E5; color: #fff; }
    `;

    if (loading) {
        return (
            <>
                <style>{animStyles}</style>
                <div data-sidebar-skin="dark" data-header-skin="light" data-navbar-brand-skin="dark" data-sidebar-state="expand">
                    <Topbar />
                    <Sidebar />
                    <DashboardSkeleton />
                </div>
            </>
        );
    }

    const overallStats = performanceData?.overall_statistics || {};
    const currentYear = new Date().getFullYear();
    const years = [currentYear, currentYear - 1, currentYear - 2];

    return (
        <>
            <style>{animStyles}</style>
            <div
                data-sidebar-skin="dark"
                data-header-skin="light"
                data-navbar-brand-skin="dark"
                data-sidebar-state="expand"
            >
                <Topbar />
                <Sidebar />

                <div className="page-wrapper">
                    <div className="content pb-0">

                        {/* ─── Page Header ─── */}
                        <AnimatedCard index={0}>
                            <div className="d-flex align-items-center justify-content-between mb-4 flex-wrap gap-2">
                                <div>
                                    <h4 className="fw-bold mb-1">Performance Dashboard</h4>
                                    <p className="text-muted mb-0 fs-13">
                                        Overview of employee performance metrics and KPIs
                                    </p>
                                </div>
                                <div className="d-flex gap-2 align-items-center">
                                    {/* Year selector */}
                                    <div ref={trendDropdownRef} style={{ position: 'relative' }}>
                                        <button
                                            className="btn btn-sm btn-outline-white border"
                                            onClick={() => setTrendYearDropdownOpen((v) => !v)}
                                        >
                                            <i className="ti ti-calendar me-1" />
                                            {trendDataYear}
                                            <i className="ti ti-chevron-down ms-1" />
                                        </button>
                                        {trendYearDropdownOpen && (
                                            <div className="year-dropdown">
                                                {years.map((y) => (
                                                    <div
                                                        key={y}
                                                        className={`year-dropdown-item${trendDataYear === y ? ' active' : ''}`}
                                                        onClick={() => {
                                                            setTrendDataYear(y);
                                                            setTrendYearDropdownOpen(false);
                                                        }}
                                                    >
                                                        {y}
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                    <button
                                        className="btn btn-sm btn-outline-white border"
                                        onClick={() => fetchData(false, trendDataYear)}
                                        disabled={refreshing}
                                        title="Refresh"
                                    >
                                        <i
                                            className="ti ti-refresh me-1"
                                            style={{
                                                display: 'inline-block',
                                                transition: 'transform 0.4s',
                                                transform: refreshing ? 'rotate(360deg)' : 'none',
                                            }}
                                        />
                                        Refresh
                                    </button>
                                </div>
                            </div>
                        </AnimatedCard>

                        {/* ─── KPI Cards ─── */}
                        <div className="row">
                            {/* Total Employees */}
                            <div className="col-md-6 col-xl-3 d-flex">
                                <AnimatedCard index={1} className="flex-fill">
                                    <div className="card flex-fill h-100">
                                        <div className="card-body">
                                            <div className="d-flex align-items-center justify-content-between">
                                                <div>
                                                    <div className="avatar avtar-lg bg-teal mb-2">
                                                        <i className="ti ti-users-group fs-24 text-white" />
                                                    </div>
                                                    <h6 className="fs-14 fw-semibold mb-1">Total Employees</h6>
                                                    <h3 className="fw-bold mb-0">
                                                        {overallStats.total_employees ?? '—'}
                                                    </h3>
                                                </div>
                                                <Link to="/hr/employees" className="btn btn-sm btn-outline-teal">
                                                    View All
                                                </Link>
                                            </div>
                                        </div>
                                    </div>
                                </AnimatedCard>
                            </div>

                            {/* Departments */}
                            <div className="col-md-6 col-xl-3 d-flex">
                                <AnimatedCard index={2} className="flex-fill">
                                    <div className="card flex-fill h-100">
                                        <div className="card-body">
                                            <div className="d-flex align-items-center justify-content-between">
                                                <div>
                                                    <div className="avatar avtar-lg bg-warning mb-2">
                                                        <i className="ti ti-building fs-24 text-white" />
                                                    </div>
                                                    <h6 className="fs-14 fw-semibold mb-1">Departments</h6>
                                                    <h3 className="fw-bold mb-0">
                                                        {overallStats.total_departments ?? '—'}
                                                    </h3>
                                                </div>
                                                <Link to="/hr/departments" className="btn btn-sm btn-outline-warning">
                                                    Manage
                                                </Link>
                                            </div>
                                        </div>
                                    </div>
                                </AnimatedCard>
                            </div>

                            {/* Cost Centers */}
                            <div className="col-md-6 col-xl-3 d-flex">
                                <AnimatedCard index={3} className="flex-fill">
                                    <div className="card flex-fill h-100">
                                        <div className="card-body">
                                            <div className="d-flex align-items-center justify-content-between">
                                                <div>
                                                    <div className="avatar avtar-lg bg-orange mb-2">
                                                        <i className="ti ti-layout-grid fs-24 text-white" />
                                                    </div>
                                                    <h6 className="fs-14 fw-semibold mb-1">Cost Centers</h6>
                                                    <h3 className="fw-bold mb-0">
                                                        {overallStats.total_cost_centers ?? '—'}
                                                    </h3>
                                                </div>
                                                <Link to="/hr/cost-centers" className="btn btn-sm btn-outline-primary">
                                                    Manage
                                                </Link>
                                            </div>
                                        </div>
                                    </div>
                                </AnimatedCard>
                            </div>

                            {/* Avg Performance */}
                            <div className="col-md-6 col-xl-3 d-flex">
                                <AnimatedCard index={4} className="flex-fill">
                                    <div className="card flex-fill h-100">
                                        <div className="card-body">
                                            <div className="d-flex align-items-center justify-content-between">
                                                <div>
                                                    <div className="avatar avtar-lg bg-success mb-2">
                                                        <i className="ti ti-trending-up fs-24 text-white" />
                                                    </div>
                                                    <h6 className="fs-14 fw-semibold mb-1">Avg Performance</h6>
                                                    <h3 className="fw-bold mb-0">
                                                        {overallStats.average_progress
                                                            ? `${overallStats.average_progress}%`
                                                            : '0%'}
                                                    </h3>
                                                </div>
                                                <span className="badge badge-soft-success fs-12">Overall</span>
                                            </div>
                                        </div>
                                    </div>
                                </AnimatedCard>
                            </div>
                        </div>

                        {/* ─── Charts Row ─── */}
                        <div className="row">
                            {/* Reporting Summary Pie */}
                            <div className="col-md-6 d-flex">
                                <AnimatedCard index={5} className="flex-fill">
                                    <div className="card flex-fill h-100">
                                        <div className="card-header">
                                            <h5 className="fw-bold mb-0">Reporting Summary</h5>
                                        </div>
                                        <div className="card-body d-flex align-items-center justify-content-center">
                                            <ResponsiveContainer width="100%" height={260}>
                                                <PieChart>
                                                    <Pie
                                                        data={reportingPieData}
                                                        cx="50%"
                                                        cy="50%"
                                                        innerRadius={60}
                                                        outerRadius={85}
                                                        paddingAngle={5}
                                                        dataKey="value"
                                                        label={renderPieLabel}
                                                        labelLine={true}
                                                    >
                                                        {reportingPieData.map((_, index) => (
                                                            <Cell
                                                                key={`rpt-cell-${index}`}
                                                                fill={REPORTING_COLORS[index % REPORTING_COLORS.length]}
                                                            />
                                                        ))}
                                                    </Pie>
                                                    <Tooltip />
                                                </PieChart>
                                            </ResponsiveContainer>
                                        </div>
                                        {/* Legend */}
                                        <div className="card-footer d-flex gap-3 justify-content-center pb-3">
                                            {reportingPieData.map((item, i) => (
                                                <span key={i} className="d-flex align-items-center gap-1 fs-13">
                                                    <span
                                                        style={{
                                                            width: 10,
                                                            height: 10,
                                                            borderRadius: '50%',
                                                            background: REPORTING_COLORS[i],
                                                            display: 'inline-block',
                                                        }}
                                                    />
                                                    {item.name}: <strong>{item.value ?? 0}</strong>
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                </AnimatedCard>
                            </div>

                            {/* Goal Status Distribution Pie */}
                            <div className="col-md-6 d-flex">
                                <AnimatedCard index={6} className="flex-fill">
                                    <div className="card flex-fill h-100">
                                        <div className="card-header">
                                            <h5 className="fw-bold mb-0">Goal Status Distribution</h5>
                                        </div>
                                        <div className="card-body d-flex align-items-center justify-content-center">
                                            {statusLoading ? (
                                                <div className="text-muted py-5 text-center">
                                                    <i className="ti ti-loader-2 fs-28 d-block mb-2" />
                                                    Loading...
                                                </div>
                                            ) : statusError ? (
                                                <div className="alert alert-danger w-100 text-center">{statusError}</div>
                                            ) : (
                                                <ResponsiveContainer width="100%" height={260}>
                                                    <PieChart>
                                                        <Pie
                                                            data={statusSummary?.status_distribution || []}
                                                            cx="50%"
                                                            cy="50%"
                                                            innerRadius={60}
                                                            outerRadius={85}
                                                            paddingAngle={5}
                                                            dataKey="count"
                                                            nameKey="status"
                                                            label={({ status }) => status}
                                                        >
                                                            {(statusSummary?.status_distribution || []).map((_, index) => (
                                                                <Cell
                                                                    key={`stat-cell-${index}`}
                                                                    fill={PIE_COLORS[index % PIE_COLORS.length]}
                                                                />
                                                            ))}
                                                        </Pie>
                                                        <Tooltip />
                                                        {/* Centre label */}
                                                        <text
                                                            x="50%"
                                                            y="47%"
                                                            textAnchor="middle"
                                                            dominantBaseline="middle"
                                                            fontSize="22"
                                                            fontWeight="bold"
                                                            fill="#4F46E5"
                                                        >
                                                            {totalGoals}
                                                        </text>
                                                        <text
                                                            x="50%"
                                                            y="57%"
                                                            textAnchor="middle"
                                                            dominantBaseline="middle"
                                                            fontSize="12"
                                                            fill="#6b7280"
                                                        >
                                                            Total Goals
                                                        </text>
                                                    </PieChart>
                                                </ResponsiveContainer>
                                            )}
                                        </div>
                                        {/* Legend */}
                                        <div className="card-footer d-flex flex-wrap gap-3 justify-content-center pb-3">
                                            {(statusSummary?.status_distribution || []).map((item, i) => (
                                                <span key={i} className="d-flex align-items-center gap-1 fs-13">
                                                    <span
                                                        style={{
                                                            width: 10,
                                                            height: 10,
                                                            borderRadius: '50%',
                                                            background: PIE_COLORS[i % PIE_COLORS.length],
                                                            display: 'inline-block',
                                                        }}
                                                    />
                                                    {item.status}: <strong>{item.count}</strong>
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                </AnimatedCard>
                            </div>
                        </div>

                        {/* ─── Recent Reviews Table ─── */}
                        <AnimatedCard index={7}>
                            <div className="card shadow">
                                <div className="card-header d-flex align-items-center justify-content-between">
                                    <h6 className="fw-bold mb-0">Recent Reviews</h6>
                                </div>
                                <div className="card-body p-0">
                                    {loadingReviews ? (
                                        <div className="text-muted text-center py-5">
                                            <i className="ti ti-loader-2 fs-28 d-block mb-2" />
                                            Loading reviews...
                                        </div>
                                    ) : reviewsError ? (
                                        <div className="alert alert-danger m-3">{reviewsError}</div>
                                    ) : !reviews || reviews.length === 0 ? (
                                        <div className="text-muted text-center py-5">
                                            <i className="ti ti-clipboard-off fs-36 d-block mb-2" />
                                            No recent reviews found.
                                        </div>
                                    ) : (
                                        <div className="table-responsive">
                                            <table className="table table-nowrap border mb-0">
                                                <thead>
                                                    <tr>
                                                        <th>Activity / KPI</th>
                                                        <th>Performance Standard / Target</th>
                                                        <th>Metric</th>
                                                        <th>Self Score</th>
                                                        <th>HOD Score</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {reviews.slice(0, 5).map((review, idx) => (
                                                        <tr key={review.id || idx}>
                                                            <td
                                                                style={{
                                                                    maxWidth: 220,
                                                                    overflow: 'hidden',
                                                                    textOverflow: 'ellipsis',
                                                                    whiteSpace: 'nowrap',
                                                                }}
                                                            >
                                                                {review.activity || '—'}
                                                            </td>
                                                            <td
                                                                style={{
                                                                    maxWidth: 280,
                                                                    overflow: 'hidden',
                                                                    textOverflow: 'ellipsis',
                                                                    whiteSpace: 'nowrap',
                                                                }}
                                                            >
                                                                {review.performance_standard || '—'}
                                                            </td>
                                                            <td
                                                                style={{
                                                                    maxWidth: 200,
                                                                    overflow: 'hidden',
                                                                    textOverflow: 'ellipsis',
                                                                    whiteSpace: 'nowrap',
                                                                }}
                                                            >
                                                                {review.metric || '—'}
                                                            </td>
                                                            <td>
                                                                <span className="badge badge-soft-primary">
                                                                    {review.score != null ? review.score : '—'}
                                                                </span>
                                                            </td>
                                                            <td>
                                                                <span className="badge badge-soft-success">
                                                                    {review.hod_score != null ? review.hod_score : '—'}
                                                                </span>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </AnimatedCard>

                    </div>

                    <div className="footer d-flex align-items-center justify-content-between flex-column flex-sm-row row-gap-2 border-top py-2 px-3">
                        <p className="text-dark mb-0">
                            2026 © <span className="link-primary">Casoe Technologies</span>, All Rights Reserved
                        </p>
                    </div>
                </div>
            </div>
        </>
    );
};

export default Dashboard;